import "../dashboard/AdminDashboard.css";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { AdminDashboardProvider, useAdminDashboardCtx } from "../hooks/AdminDashboardContext";
import { AdminSidebar } from "../components/sidebar/AdminSidebar";
import HospitalManagementView from "../screens/hospital-management/HospitalManagementView";
import DoctorManagementView from "../screens/doctor-management/DoctorManagementView";
import { MasterListScreen } from "../screens/MasterListScreen";
import CreateLoginScreen from "../screens/CreateLoginScreen";
import AdminHospitalListScreen from "../screens/user-management/AdminHospitalListScreen";
import AdminDoctorListScreen from "../screens/user-management/AdminDoctorListScreen";
import AnalyticsDashboardScreen from "../screens/AnalyticsDashboardScreen";
import HealthTrackerScreen from "../screens/HealthTrackerScreen";
import OemMasterScreen from "../screens/OemMasterScreen";
import D2dCommunicationScreen from "../screens/D2dCommunicationScreen";
import D2dChatScreen from "../screens/D2dChatScreen";
import {
  LOGIN_ADMIN_PAGE_TITLE,
  LOGIN_ADMIN_DESCRIPTIONS,
  DASHBOARD_STEP,
  HEALTH_TRACKER_STEP,
} from "../constants/menuConfig";

const AdminDashboardContent = () => {
  const {
    currentStep,
    validated,
    STEPS,
    STEP_DESCRIPTIONS,
    sidebarOpen,
    setSidebarOpen,
    expandedGroups,
    expandedSections,
    toggleMenuGroup,
    toggleMenuSection,
    selectStep,
    handleSubmit,
    renderForm,
    establishments,
    accreditations,
    submitMsg,
    createLoginDoctor,
    editingPatientId,
    handleCancelPatientEdit,
    editingUserId,
    handleCancelUserEdit,
    establishmentLoginForm,
    establishmentLogins,
    doctorLoginForm,
    doctorLogins,
    doctors,  
    loginPasswordsById,
    savingEstablishmentLogin,
    savingDoctorLogin,
    userTypes,
    locations,
    handleCreateLoginEstablishmentChange,
    handleCreateLoginDoctorChange,
    handleEstablishmentLoginChange,
    handleDoctorLoginChange,
    handleSaveEstablishmentLogin,
    handleSaveDoctorLogin,
    handleSaveLocationLogin,
    handleEditEstablishmentLogin,
    handleCancelEstablishmentLoginEdit,
    handleDeleteEstablishmentLogin,
    handleEditDoctorLogin,
    handleCancelDoctorLoginEdit,
    handleDeleteDoctorLogin,
    handleEditLocationLogin,
    handleCancelLocationLoginEdit,
    handleDeleteLocationLogin,
    handleEditUserLogin,
    handleCancelUserLoginEdit,
    handleDeleteUserLogin,
    locationLoginForm,
    locationLogins,
    handleLocationLoginChange,
    savingLocationLogin,
    isLoginAdminStep,
    LOGIN_ADMIN_STEPS,
    createLoginUser,
    users,
    userLoginForm,
    userLogins,
    savingUserLogin,
    handleCreateLoginUserChange,
    handleUserLoginChange,
    handleSaveUserLogin,
    handleCloseUserLogin,
  } = useAdminDashboardCtx();

  const isDashboardScreen = currentStep === DASHBOARD_STEP;
  const isHealthTrackerScreen = currentStep === HEALTH_TRACKER_STEP;
  const isFullBleedScreen = isDashboardScreen || isHealthTrackerScreen;
  const isLoginAdminScreen = isLoginAdminStep(currentStep);
  const isOemScreen = currentStep === 72;
  const isD2dScreen = currentStep === 73;
  const isD2dChatScreen = currentStep === 74;
  const pageTitle = isDashboardScreen
    ? "Dashboard"
    : isHealthTrackerScreen
    ? "Health Tracker"
    : isLoginAdminScreen
    ? LOGIN_ADMIN_PAGE_TITLE
    : STEPS[currentStep];
  const pageDescription = isLoginAdminScreen
    ? (LOGIN_ADMIN_DESCRIPTIONS[currentStep] || "Create login credentials for system users.")
    : isHealthTrackerScreen
    ? "Track member care journey, health credits, and care coins."
    : STEP_DESCRIPTIONS[currentStep];
  const loginAdminMode = LOGIN_ADMIN_STEPS[currentStep];

  const loginScreenProps = (() => {
    if (loginAdminMode === "location") {
      return {
        mode: "location",
        form: locationLoginForm,
        onChange: handleLocationLoginChange,
        savedLogins: locationLogins,
        onSave: handleSaveLocationLogin,
        onEdit: handleEditLocationLogin,
        onDelete: handleDeleteLocationLogin,
        onCancelEdit: handleCancelLocationLoginEdit,
        saving: savingLocationLogin,
      };
    }
    if (loginAdminMode === "doctor") {
      return {
        mode: "doctor",
        doctorProfile: createLoginDoctor,
        form: doctorLoginForm,
        onChange: handleDoctorLoginChange,
        savedLogins: doctorLogins,
        onSave: handleSaveDoctorLogin,
        onEdit: handleEditDoctorLogin,
        onDelete: handleDeleteDoctorLogin,
        onCancelEdit: handleCancelDoctorLoginEdit,
        saving: savingDoctorLogin,
        selectedDoctorId: doctorLoginForm.doctor_id,
        onDoctorChange: handleCreateLoginDoctorChange,
      };
    }
    if (loginAdminMode === "user") {
      return {
        mode: "user",
        userProfile: createLoginUser,
        users,
        form: userLoginForm,
        onChange: handleUserLoginChange,
        savedLogins: userLogins,
        onSave: handleSaveUserLogin,
        onEdit: handleEditUserLogin,
        onDelete: handleDeleteUserLogin,
        onCancelEdit: handleCancelUserLoginEdit,
        onClose: handleCloseUserLogin,
        saving: savingUserLogin,
        selectedUserId: createLoginUser?.user_id,
        onUserChange: handleCreateLoginUserChange,
      };
    }
    return {
      mode: "establishment",
      form: establishmentLoginForm,
      onChange: handleEstablishmentLoginChange,
      savedLogins: establishmentLogins,
      onSave: handleSaveEstablishmentLogin,
      onEdit: handleEditEstablishmentLogin,
      onDelete: handleDeleteEstablishmentLogin,
      onCancelEdit: handleCancelEstablishmentLoginEdit,
      saving: savingEstablishmentLogin,
      selectedEstablishmentId: establishmentLoginForm.establishment_id,
      onEstablishmentChange: handleCreateLoginEstablishmentChange,
    };
  })();

  return (
    <section className="authdata-section">
      <Container fluid className="authdata-container">
        <Row className="authdata-row">
          <div className="authdata-mobile-bar">
            <button
              className="authdata-hamburger"
              onClick={() => setSidebarOpen((o) => !o)}
              aria-label="Toggle menu"
            >
              <span /><span /><span />
            </button>
            <span className="authdata-mobile-title">{pageTitle}</span>
          </div>

          {sidebarOpen && (
            <div
              className="authdata-overlay"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          <AdminSidebar
            sidebarOpen={sidebarOpen}
            currentStep={currentStep}
            expandedGroups={expandedGroups}
            expandedSections={expandedSections}
            toggleMenuGroup={toggleMenuGroup}
            toggleMenuSection={toggleMenuSection}
            selectStep={selectStep}
          />

          <Col
            md={9}
            lg={10}
            className={`authdata-right${isFullBleedScreen ? " authdata-right--dashboard" : ""}`}
          >
            {isDashboardScreen ? (
              <AnalyticsDashboardScreen />
            ) : isHealthTrackerScreen ? (
              <HealthTrackerScreen />
            ) : (
            <div className={`authdata-form-wrapper${
              currentStep === 37 || currentStep === 38 || currentStep === 48 || currentStep === 68 || currentStep === 72 || currentStep === 73 || currentStep === 74 ? " authdata-form-wrapper--em" : ""
            }${isLoginAdminScreen ? " authdata-form-wrapper--cl" : ""}${
              currentStep === 43 ? " authdata-form-wrapper--hm" : ""
            }${currentStep === 44 ? " authdata-form-wrapper--dm" : ""}${
              currentStep === 66 || currentStep === 67 ? " authdata-form-wrapper--list" : ""
            }`}>
              {currentStep !== 43 && currentStep !== 44 && currentStep !== 66 && currentStep !== 67 && (
                <div className="authdata-form-header">
                  <h3>{pageTitle}</h3>
                  <p>{pageDescription}</p>
                </div>
              )}

              {isOemScreen ? (
                <OemMasterScreen />
              ) : isD2dScreen ? (
                <D2dCommunicationScreen />
              ) : isD2dChatScreen ? (
                <D2dChatScreen />
              ) : isLoginAdminScreen ? (
                <CreateLoginScreen
                  establishments={establishments}
                  doctors={doctors}
                  userTypes={userTypes}
                  locations={locations}
                  loginPasswordsById={loginPasswordsById}
                  {...loginScreenProps}
                />
              ) : (
                <>
                  <Form
                    noValidate
                    validated={validated}
                    onSubmit={handleSubmit}
                    className="authdata-form"
                  >
                    {currentStep !== 39 && currentStep !== 42 && currentStep !== 43
                      && currentStep !== 66 && currentStep !== 67 && (
                      <>
                        <div className="authdata-form-body">{renderForm()}</div>

                        <div className="authdata-form-actions">
                          {currentStep === 48 && editingPatientId && (
                            <Button
                              type="button"
                              variant="outline-secondary"
                              className="me-2"
                              onClick={handleCancelPatientEdit}
                            >
                              Cancel Edit
                            </Button>
                          )}
                          {currentStep === 68 && editingUserId && (
                            <Button
                              type="button"
                              variant="outline-secondary"
                              className="me-2"
                              onClick={handleCancelUserEdit}
                            >
                              Cancel Edit
                            </Button>
                          )}
                          <Button type="submit" variant="success" className="btn-submit">
                            {currentStep === 38
                              ? "Save Doctor"
                              : currentStep === 48
                                ? (editingPatientId ? "Update Patient" : "Save Patient")
                                : currentStep === 68
                                  ? (editingUserId ? "Update User" : "Save User")
                                  : "Save"}
                          </Button>
                        </div>
                      </>
                    )}
                  </Form>

                  {currentStep === 43 && (
                    <HospitalManagementView
                      establishments={establishments}
                      accreditations={accreditations}
                      onEdit={() => selectStep(37)}
                    />
                  )}

                  {currentStep === 44 && (
                    <DoctorManagementView onEdit={() => selectStep(38)} useDemoFallback />
                  )}

                  {currentStep === 66 && <AdminHospitalListScreen />}
                  {currentStep === 67 && <AdminDoctorListScreen />}

                  <MasterListScreen />

                  {submitMsg && (currentStep === 38 || currentStep === 68 || currentStep === 69) && (
                    <div
                      className={`authdata-msg ${submitMsg.startsWith("Error") ? "error" : "success"}`}
                    >
                      {submitMsg}
                    </div>
                  )}
                </>
              )}

              {submitMsg && isLoginAdminScreen && (
                <div
                  className={`authdata-msg ${submitMsg.startsWith("Error") ? "error" : "success"}`}
                >
                  {submitMsg}
                </div>
              )}
            </div>
            )}
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export const AdminDashboardPage = () => (
  <AdminDashboardProvider>
    <AdminDashboardContent />
  </AdminDashboardProvider>
);

export const AuthDataPage = AdminDashboardPage;
